import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";

import { FIRM } from "@/lib/firm";

/**
 * Queensland transfer (stamp) duty estimator.
 * Rates: Duties Act 2001 (Qld) sch 3 general rate, s 91 home concession,
 * s 92 first home concession (contracts from 9 June 2024), s 93 first home
 * vacant land concession, and AFAD (additional foreign acquirer duty) at 8%.
 */

function generalDuty(value: number): number {
  if (value <= 5_000) return 0;
  if (value <= 75_000) return ((value - 5_000) / 100) * 1.5;
  if (value <= 540_000) return 1_050 + ((value - 75_000) / 100) * 3.5;
  if (value <= 1_000_000) return 17_325 + ((value - 540_000) / 100) * 4.5;
  return 38_025 + ((value - 1_000_000) / 100) * 5.75;
}

function homeConcessionDuty(value: number): number {
  if (value <= 350_000) return (value / 100) * 1;
  if (value <= 540_000) return 3_500 + ((value - 350_000) / 100) * 3.5;
  if (value <= 1_000_000) return 10_150 + ((value - 540_000) / 100) * 4.5;
  return 30_850 + ((value - 1_000_000) / 100) * 5.75;
}

/** First home concession rebate applied to the home-concession duty. */
function firstHomeRebate(value: number): number {
  if (value < 700_000) return Number.POSITIVE_INFINITY; // full offset — duty nil
  if (value < 720_000) return 17_350;
  if (value < 740_000) return 15_615;
  if (value < 760_000) return 13_880;
  if (value < 780_000) return 12_145;
  if (value < 800_000) return 10_410;
  return 0;
}

/** First home vacant land concession: full to $350k, phasing out to $500k. */
function vacantLandDuty(value: number): number {
  const full = generalDuty(value);
  if (value < 350_000) return 0;
  if (value >= 500_000) return full;
  const rebateTable: [number, number][] = [
    [400_000, 7_175],
    [450_000, 3_500],
    [500_000, 1_575],
  ];
  const rebate = rebateTable.find(([cap]) => value < cap)?.[1] ?? 0;
  return Math.max(0, full - rebate);
}

/** Titles Registry transfer lodgement fee (indicative scale). */
function transferRegistrationFee(value: number): number {
  const base = 231.13;
  if (value <= 180_000) return base;
  return base + Math.ceil((value - 180_000) / 10_000) * 43.29;
}

const currency = (n: number) =>
  n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

type Buyer = "investor" | "owner-occupier" | "first-home";

const BUYER_OPTIONS: { value: Buyer; label: string; hint: string }[] = [
  { value: "investor", label: "Investment or second property", hint: "General rate — no concession" },
  { value: "owner-occupier", label: "I will live in it", hint: "Home concession" },
  { value: "first-home", label: "First home buyer", hint: "First home concession" },
];

export function DutyCalculator() {
  const [price, setPrice] = useState(750_000);
  const [buyer, setBuyer] = useState<Buyer>("owner-occupier");
  const [newBuild, setNewBuild] = useState(false);
  const [vacantLand, setVacantLand] = useState(false);
  const [foreign, setForeign] = useState(false);

  const result = useMemo(() => {
    const value = Number.isFinite(price) && price > 0 ? price : 0;
    let duty: number;
    let basis: string;

    if (buyer === "first-home" && vacantLand) {
      duty = vacantLandDuty(value);
      basis =
        "First home vacant land concession — full concession under $350,000, phasing out to $500,000.";
    } else if (buyer === "first-home" && newBuild) {
      duty = 0;
      basis =
        "First home buyers of a brand-new home or off-the-plan purchase pay no transfer duty on contracts signed from 1 May 2025, with no value cap.";
    } else if (buyer === "first-home") {
      const concessional = homeConcessionDuty(value);
      const rebate = firstHomeRebate(value);
      duty = rebate === Number.POSITIVE_INFINITY ? 0 : Math.max(0, concessional - rebate);
      basis =
        value < 700_000
          ? "First home concession — the rebate fully offsets duty under $700,000."
          : value < 800_000
            ? "First home concession — partial rebate applies between $700,000 and $800,000."
            : "Above $800,000 the first home rebate cuts out, so the home concession rate applies.";
    } else if (buyer === "owner-occupier") {
      duty = homeConcessionDuty(value);
      basis =
        "Home concession — $1.00 per $100 on the first $350,000, then general rates. You must move in within 12 months and not dispose of it for 12 months after that.";
    } else {
      duty = generalDuty(value);
      basis = "General transfer duty rate — no concession applies to investment property.";
    }

    const surcharge = foreign ? value * 0.08 : 0;
    const registration = transferRegistrationFee(value);
    const legalFee = 1_499;
    const searchesLow = 350;
    const searchesHigh = 750;

    return {
      value,
      duty,
      basis,
      surcharge,
      registration,
      legalFee,
      searchesLow,
      searchesHigh,
      totalLow: duty + surcharge + registration + legalFee + searchesLow,
      totalHigh: duty + surcharge + registration + legalFee + searchesHigh,
    };
  }, [price, buyer, newBuild, vacantLand, foreign]);

  return (
    <section
      id="calculator"
      className="mb-14 scroll-mt-24 overflow-hidden rounded-sm border border-border bg-card"
    >
      <div className="border-b border-border bg-secondary/50 px-6 py-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-accent">
          Free tool · Queensland
        </p>
        <h2 className="mt-2 font-serif text-2xl text-foreground">Transfer duty calculator</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Estimate your Queensland transfer (stamp) duty, plus what the whole purchase costs once
          registry fees, searches and our fixed legal fee are added.
        </p>
      </div>

      <div className="grid gap-8 px-6 py-7 md:grid-cols-2">
        <div>
          <label
            htmlFor="duty-price"
            className="block text-sm font-semibold text-foreground"
          >
            Purchase price
          </label>
          <div className="mt-2 flex items-center rounded-sm border border-border bg-background px-3 focus-within:border-primary">
            <span className="text-base text-muted-foreground">$</span>
            <input
              id="duty-price"
              type="number"
              inputMode="numeric"
              min={0}
              step={5000}
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              className="w-full bg-transparent px-2 py-3 text-base text-foreground outline-none"
            />
          </div>
          <input
            aria-label="Purchase price slider"
            type="range"
            min={100_000}
            max={2_500_000}
            step={10_000}
            value={Math.min(Math.max(price || 0, 100_000), 2_500_000)}
            onChange={(e) => setPrice(Number(e.target.value))}
            className="mt-4 w-full accent-primary"
          />

          <fieldset className="mt-7">
            <legend className="text-sm font-semibold text-foreground">Who is buying?</legend>
            <div className="mt-3 space-y-2">
              {BUYER_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-sm border p-3 transition-colors ${
                    buyer === opt.value
                      ? "border-primary bg-secondary/60"
                      : "border-border hover:bg-secondary/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="duty-buyer"
                    value={opt.value}
                    checked={buyer === opt.value}
                    onChange={() => setBuyer(opt.value)}
                    className="mt-1 accent-primary"
                  />
                  <span>
                    <span className="block text-sm font-medium text-foreground">{opt.label}</span>
                    <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="mt-6 space-y-3">
            {buyer === "first-home" && (
              <>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={newBuild}
                    onChange={(e) => {
                      setNewBuild(e.target.checked);
                      if (e.target.checked) setVacantLand(false);
                    }}
                    className="mt-0.5 accent-primary"
                  />
                  <span>
                    Brand-new home or off the plan{" "}
                    <span className="text-xs">(contracts from 1 May 2025 — duty free)</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={vacantLand}
                    onChange={(e) => {
                      setVacantLand(e.target.checked);
                      if (e.target.checked) setNewBuild(false);
                    }}
                    className="mt-0.5 accent-primary"
                  />
                  <span>Vacant land I will build my first home on</span>
                </label>
              </>
            )}
            <label className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={foreign}
                onChange={(e) => setForeign(e.target.checked)}
                className="mt-0.5 accent-primary"
              />
              <span>
                Foreign buyer, trust or company{" "}
                <span className="text-xs">(8% additional foreign acquirer duty)</span>
              </span>
            </label>
          </div>
        </div>

        <div>
          <div className="rounded-sm border border-border bg-secondary/40 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Estimated transfer duty
            </p>
            <p className="mt-1 font-serif text-4xl text-foreground">{currency(result.duty)}</p>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{result.basis}</p>
          </div>

          <dl className="mt-5 space-y-2.5 text-sm">
            <Row label="Transfer duty" value={currency(result.duty)} />
            {result.surcharge > 0 && (
              <Row label="Foreign acquirer duty (8%)" value={currency(result.surcharge)} />
            )}
            <Row
              label="Titles Registry transfer fee"
              value={currency(result.registration)}
            />
            <Row
              label="Searches and PEXA (typical range)"
              value={`${currency(result.searchesLow)}–${currency(result.searchesHigh)}`}
            />
            <Row
              label={`Our fixed legal fee (purchase, incl. GST)`}
              value={currency(result.legalFee)}
            />
            <div className="flex items-baseline justify-between border-t border-border pt-3">
              <dt className="text-sm font-semibold text-foreground">
                Estimated total on top of the price
              </dt>
              <dd className="text-right font-serif text-lg text-foreground">
                {currency(result.totalLow)}–{currency(result.totalHigh)}
              </dd>
            </div>
          </dl>

          <div className="mt-6 flex flex-wrap gap-2">
            <a
              href="#consultation"
              className="inline-flex items-center justify-center rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Get a written quote
            </a>
            <a
              href={FIRM.phoneHref}
              className="inline-flex items-center justify-center rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              Call {FIRM.phone}
            </a>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            An estimate only, based on current Queensland rates. Duty is assessed on the greater of
            the price and the market value, and concessions have eligibility and residence
            conditions. See our{" "}
            <Link to="/fees" className="font-semibold text-primary underline underline-offset-4">
              full fees and disbursements
            </Link>{" "}
            page, or call us and we will confirm the exact figure for your contract.
          </p>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="shrink-0 font-medium text-foreground">{value}</dd>
    </div>
  );
}

export default DutyCalculator;
